import { useMemo } from "react";
import { LOG_CATEGORY_DEFS } from "../adminConstants";
import {
  buildLogSentence,
  getLogCategory,
  normalizeLogSeverity,
} from "../adminUtils";

const useAdminLogs = ({
  auditLog,
  partners,
  locations,
  logSearch,
  logDateFrom,
  logDateTo,
  logCategoryFilter,
  logLocationFilter,
  logPartnerFilter,
}) => {
  const logCategoryOptions = LOG_CATEGORY_DEFS;
  const logLocationOptions = useMemo(
    () => [
      { value: "all", label: "All locations" },
      ...locations.map((location) => ({
        value: location.id,
        label: location.name,
      })),
    ],
    [locations]
  );
  const logPartnerOptions = useMemo(
    () => [
      { value: "all", label: "All partners" },
      ...partners.map((partner) => ({
        value: partner.id,
        label: partner.name,
      })),
    ],
    [partners]
  );
  const logEntries = useMemo(() => {
    return auditLog.map((entry) => {
      const category = getLogCategory(entry);
      const severityTone = normalizeLogSeverity(entry.severity);
      const haystack = `${entry.action} ${entry.target} ${entry.detail} ${entry.detailJson || ""} ${entry.actor}`.toLowerCase();
      return {
        ...entry,
        categoryId: category.id,
        categoryLabel: category.label,
        categoryIcon: category.icon,
        severityTone,
        summary: buildLogSentence(entry),
        searchText: haystack,
      };
    });
  }, [auditLog]);
  const logFilters = useMemo(() => {
    const from = logDateFrom ? new Date(logDateFrom) : null;
    const to = logDateTo ? new Date(logDateTo) : null;
    return { from, to };
  }, [logDateFrom, logDateTo]);
  const filteredLogEntries = useMemo(() => {
    const query = logSearch.trim().toLowerCase();
    const activeCategoryIds =
      logCategoryFilter.length > 0 ? new Set(logCategoryFilter) : null;
    const locationLabel =
      logLocationFilter === "all"
        ? ""
        : logLocationOptions.find((item) => item.value === logLocationFilter)?.label || "";
    const partnerLabel =
      logPartnerFilter === "all"
        ? ""
        : logPartnerOptions.find((item) => item.value === logPartnerFilter)?.label || "";
    const locationNeedle = locationLabel.toLowerCase();
    const partnerNeedle = partnerLabel.toLowerCase();
    return logEntries.filter((entry) => {
      if (query && !entry.searchText.includes(query)) return false;
      if (logFilters.from && entry.createdUtc) {
        if (new Date(entry.createdUtc) < logFilters.from) return false;
      }
      if (logFilters.to && entry.createdUtc) {
        if (new Date(entry.createdUtc) > logFilters.to) return false;
      }
      if (activeCategoryIds && !activeCategoryIds.has(entry.categoryId)) return false;
      if (locationNeedle && !entry.searchText.includes(locationNeedle)) return false;
      if (partnerNeedle && !entry.searchText.includes(partnerNeedle)) return false;
      return true;
    });
  }, [
    logSearch,
    logCategoryFilter,
    logLocationFilter,
    logPartnerFilter,
    logEntries,
    logFilters,
    logLocationOptions,
    logPartnerOptions,
  ]);

  return {
    logCategoryOptions,
    logLocationOptions,
    logPartnerOptions,
    filteredLogEntries,
  };
};

export default useAdminLogs;
